import {saveAs} from 'file-saver';

import {
	configEditorDeployApi as deployApi,
	configEditorDefaultAssets as defaultAssets,
	configEditorGenericProductAssets as genericProductAssets,
} from '../../../api';
import {getScopedAsyncAction, provideAuthorizationToken} from '../util/async';
import {
	CONFIG_EDITOR__ACTIONSCOPE,
	INITIALIZE_ASSETS__ACTIONTYPE,
	ADD_ASSETS__ACTIONTYPE,
	REMOVE_ASSETS__ACTIONTYPE,
	SWITCH_TO_ASSET__ACTIONTYPE,
	CHANGE_ASSET__ACTIONTYPE,
	SAVE_ASSET__ACTIONTYPE,
	REVERT_ASSET__ACTIONTYPE,
	LOAD_ASSETS__ACTIONTYPES,
	RESET_ASSETS__ACTIONTYPE,
	CLEAR_LOAD_RESULT__ACTIONTYPE,
	EXPORT_ASSETS__ACTIONTYPES,
	IMPORT_ASSETS__ACTIONTYPES,
	VALIDATE_ASSETS__ACTIONTYPES,
	SET_RATER_TYPE__ACTIONTYPE,
} from '../../constants/config-editor';

export function setRaterType(raterType: RaterType): ConfigEditor.SetRaterTypeAction {
	return {
		type: SET_RATER_TYPE__ACTIONTYPE,
		raterType,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function initializeAssets(): ConfigEditor.InitializeAssetsAction {
	return {
		type: INITIALIZE_ASSETS__ACTIONTYPE,
		initializeAssetsData: defaultAssets,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function addAssets(
	modifying: ConfigEditor.ModificationEntity,
	modification: ConfigEditor.AssetsModification,
): ConfigEditor.AddAssetsAction {
	return {
		type: ADD_ASSETS__ACTIONTYPE,
		addAssetsData: {
			modifying,
			modification,
			genericProductAssets,
		},
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function removeAssets(
	modifying: ConfigEditor.ModificationEntity,
	modification: ConfigEditor.AssetsModification,
): ConfigEditor.RemoveAssetsAction {
	return {
		type: REMOVE_ASSETS__ACTIONTYPE,
		removeAssetsData: {
			modifying,
			modification,
		},
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function switchToAsset(path: string): ConfigEditor.SwitchToAssetAction {
	return {
		type: SWITCH_TO_ASSET__ACTIONTYPE,
		switchToAssetData: {path},
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function changeAsset(contents: string): ConfigEditor.ChangeAssetAction {
	return {
		type: CHANGE_ASSET__ACTIONTYPE,
		changeAssetData: {contents},
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function saveAsset(): ConfigEditor.SaveAssetAction {
	return {
		type: SAVE_ASSET__ACTIONTYPE,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function revertAsset(): ConfigEditor.RevertAssetAction {
	return {
		type: REVERT_ASSET__ACTIONTYPE,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function loadAssets(
	action: ConfigEditor.LoadAssetsActionType,
	externalServiceIntegrations?: ExternalServiceIntegrationRequest[],
): AsyncAction<ConfigEditor.DeployResponse> {
	return getScopedAsyncAction(LOAD_ASSETS__ACTIONTYPES, CONFIG_EDITOR__ACTIONSCOPE, (getState: () => StudioState) => {
		const {pending, loaded} = getState().configEditor.assets;
		const actionToTake: ConfigEditor.LoadAssetsActionType = !!loaded ? action : 'RECREATE_TENANT';
		const unauthenticatedRequest = externalServiceIntegrations
			? {externalServiceIntegrations, assets: pending, recreate: actionToTake === 'RECREATE_TENANT'}
			: {assets: pending, recreate: actionToTake === 'RECREATE_TENANT'};
		const request = provideAuthorizationToken<ConfigEditor.DeployRequest>(unauthenticatedRequest, getState());
		return deployApi.deployAssetsAndUpdateExternalServiceIntegrations(request);
	});
}

export function resetAssets(): ConfigEditor.ResetAssetsAction {
	return {
		type: RESET_ASSETS__ACTIONTYPE,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function clearLoadResult(): ConfigEditor.ClearLoadResultAction {
	return {
		type: CLEAR_LOAD_RESULT__ACTIONTYPE,
		scope: CONFIG_EDITOR__ACTIONSCOPE,
	};
}

export function openLogFile(): ThunkAction<void> {
	return (dispatch, getState) => {
		const result = getState().configEditor.assets.result;
		if (result) {
			window.open(`/api/admin/log/${result.logfile}`);
		}
	};
}

export function exportAssets(): AsyncAction<void> {
	return getScopedAsyncAction(EXPORT_ASSETS__ACTIONTYPES, CONFIG_EDITOR__ACTIONSCOPE, getState => {
		const assets = getState().configEditor.assets.pending;
		const request = provideAuthorizationToken<ConfigEditor.RequestWithAssets>({assets}, getState());
		const fileName = `${getState().global.user.username}-configeditor.zip`;
		return deployApi.exportAssets(request)
			.then(file => saveAs(file, fileName));
	});
}

export function importAssets(zipFile: File): AsyncAction<ConfigEditor.AssetFiles> {
	return getScopedAsyncAction(IMPORT_ASSETS__ACTIONTYPES, CONFIG_EDITOR__ACTIONSCOPE, getState => {
		const request = provideAuthorizationToken<ConfigEditor.ImportRequest>({zipFile}, getState());
		return deployApi.importAssets(request);
	});
}

export function validateAssets(): AsyncAction<ConfigEditor.AssetFiles> {
	return getScopedAsyncAction(VALIDATE_ASSETS__ACTIONTYPES, CONFIG_EDITOR__ACTIONSCOPE, getState => {
		const assets = getState().configEditor.assets.pending;
		const request = provideAuthorizationToken<ConfigEditor.RequestWithAssets>({assets}, getState());
		return deployApi.validateAssets(request);
	});
}

export function validateAsset(): AsyncAction<ConfigEditor.AssetFiles> {
	return getScopedAsyncAction(VALIDATE_ASSETS__ACTIONTYPES, CONFIG_EDITOR__ACTIONSCOPE, getState => {
		const {path, contents} = getState().configEditor.assets.editing;
		const assets = {[path]: contents};
		const request = provideAuthorizationToken<ConfigEditor.RequestWithAssets>({assets}, getState());
		return deployApi.validateAssets(request);
	});
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/store/actions/config-editor/assets.ts